/**
 * 予約保存リクエスト監視ロガー
 *
 * 目的:
 *   `POST /api/dispatch/reservations` を呼ぶ全経路から送信ペイロード + 呼び出し元 stack を
 *   サーバとブラウザ両方に記録する。「金額が 0 で上書きされる」バグの発生元特定用。
 *
 * 動作:
 *   - localStorage に直近 200 件をリングバッファで保持（リロード後も確認可能）
 *   - サーバ `/sys/api/log_reservation_save.php` に fire-and-forget で送信（PC 横断集約用）
 *   - suspect (price 系のどれかが 0 かつ status が done/in_use) は console.warn で強調
 *
 * エクスポート:
 *   - DevTools コンソールで `window.__exportReservationSaveLog__()` を実行すると
 *     localStorage の全ログを JSON ダウンロード
 *   - `window.__clearReservationSaveLog__()` で localStorage クリア
 *
 * 注意:
 *   - 完全に fire-and-forget。ロギング失敗は本処理に影響させない
 *   - サーバログ (NDJSON) は /sys/logs/reservation_save_YYYY-MM-DD.log
 */

import { SYS_API_BASE } from '../constants/calendar';

const STORAGE_KEY = '__reservation_save_log__';
const RING_BUFFER_SIZE = 200;
const STACK_LINES = 15;

const PRICE_FIELDS = [
  'base_price',
  'option_price',
  'noc_price',
  'cdw_price',
  'sum_price',
  'point',
  'days',
  'nights',
  'option_items_total',
] as const;

type SaveFnName =
  | 'saveDispatchReservation'
  | 'saveReservationStaff'
  | 'toggleReservationLicense'
  | 'updateReservationCommunicationTools';

interface PriceSnapshot {
  base_price: number | null;
  option_price: number | null;
  noc_price: number | null;
  cdw_price: number | null;
  sum_price: number | null;
  point: number | null;
  days: number | null;
  nights: number | null;
  option_items_total: number | null;
}

export interface ReservationSaveLogEntry {
  ts: string;
  fn: SaveFnName;
  reservation_id: number | string | null;
  status_input: any;
  status_resolved: number | null;
  customer_name: string | null;
  price_fields: PriceSnapshot;
  has_detail: boolean;
  // suspect: 価格 (base_price / sum_price / option_price のどれか) が 0 で、
  // かつ status が done(2) / in_use(3) になっている場合に true
  suspect: boolean;
  stack: string;
  payload: any;
  user_agent: string;
  page_url: string;
}

// status_id → 名前 (現行の status マッピングに依存しすぎないようヒューリスティック)
// 既知: 0=temp, 1=booked, 2=done(貸出済み), 3=in_use(利用中), 4=returned 等
// suspect 判定には主に 2/3 を対象にする
const STATUS_DONE_OR_IN_USE = (statusResolved: number | null, statusInput: any): boolean => {
  if (statusResolved === 2 || statusResolved === 3) return true;
  const s = String(statusInput ?? '').toLowerCase();
  return s === 'done' || s === 'in_use' || s === '貸出済み' || s === '利用中';
};

const pickNum = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const extractPriceFields = (payload: any): PriceSnapshot => {
  const detail = payload?.detail ?? {};
  const snap: any = {};
  PRICE_FIELDS.forEach((key) => {
    snap[key] = pickNum(payload?.[key] ?? detail?.[key]);
  });
  return snap as PriceSnapshot;
};

const isSuspect = (
  prices: PriceSnapshot,
  statusResolved: number | null,
  statusInput: any,
): boolean => {
  if (!STATUS_DONE_OR_IN_USE(statusResolved, statusInput)) return false;
  // 価格系のうち base_price / sum_price / option_price のどれかが 0 だったら疑う
  const key0 = (v: number | null) => v === 0;
  return key0(prices.base_price) || key0(prices.sum_price) || key0(prices.option_price);
};

const captureStack = (): string => {
  const err = new Error('reservation_save_capture');
  const raw = err.stack || '';
  // 最初の数行はこのファイル内のフレームなので、呼び出し元が見える範囲に絞る
  return raw.split('\n').slice(0, STACK_LINES).join('\n');
};

const readBuffer = (): ReservationSaveLogEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const writeBuffer = (buf: ReservationSaveLogEntry[]): void => {
  try {
    const trimmed = buf.slice(-RING_BUFFER_SIZE);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // 容量超過は無視
  }
};

const sendToServer = (entry: ReservationSaveLogEntry): void => {
  try {
    // fire-and-forget。失敗しても無視
    const body = JSON.stringify(entry);
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(`${SYS_API_BASE}/log_reservation_save.php`, blob);
      return;
    }
    fetch(`${SYS_API_BASE}/log_reservation_save.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* ignore */ });
  } catch {
    // ignore
  }
};

/**
 * 予約保存リクエストを記録する。
 *
 * 呼び出し側は payload と meta を渡すだけでよい。例:
 *   recordReservationSave('saveDispatchReservation', payload, { input: reservationInput });
 *
 * 注意: payload は buildDispatchReservationPayload() などで最終整形された
 *       「実際にサーバへ送る body」を渡すこと。
 */
export const recordReservationSave = (
  fn: SaveFnName,
  payload: any,
  meta?: { statusInputRaw?: any },
): void => {
  try {
    const prices = extractPriceFields(payload);
    const statusResolved = pickNum(payload?.status);
    const statusInput = meta?.statusInputRaw ?? payload?.status;
    const suspect = isSuspect(prices, statusResolved, statusInput);

    const entry: ReservationSaveLogEntry = {
      ts: new Date().toISOString(),
      fn,
      reservation_id: payload?.id ?? null,
      status_input: statusInput,
      status_resolved: statusResolved,
      customer_name: payload?.customer_name ?? null,
      price_fields: prices,
      has_detail: !!payload?.detail && Object.keys(payload.detail).length > 0,
      suspect,
      stack: captureStack(),
      payload,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      page_url: typeof window !== 'undefined' ? window.location.href : '',
    };

    // 1) localStorage に追記
    const buf = readBuffer();
    buf.push(entry);
    writeBuffer(buf);

    // 2) サーバに送信
    sendToServer(entry);

    // 3) suspect は console.warn で目立たせる
    if (suspect) {
      // eslint-disable-next-line no-console
      console.warn(
        '🚨 [reservationSaveLogger] SUSPECT save: id=%s status=%s prices=%o\nstack=\n%s',
        entry.reservation_id,
        entry.status_resolved,
        entry.price_fields,
        entry.stack,
      );
    } else if (process.env.NODE_ENV === 'development') {
      // 開発時は通常 save も簡易表示（本番では出さない）
      // eslint-disable-next-line no-console
      console.debug(
        '[reservationSaveLogger]',
        entry.fn,
        'id=', entry.reservation_id,
        'status=', entry.status_resolved,
        'prices=', entry.price_fields,
      );
    }
  } catch (e) {
    // ログ自体の失敗は無視（本処理を絶対に妨げない）
  }
};

/**
 * 開発者向けユーティリティ: localStorage に溜まったログを JSON ダウンロード
 */
const exportLog = (): void => {
  const buf = readBuffer();
  const blob = new Blob([JSON.stringify(buf, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  a.download = `reservation_save_log_${ts}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  // eslint-disable-next-line no-console
  console.log(`[reservationSaveLogger] exported ${buf.length} entries`);
};

const clearLog = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    // eslint-disable-next-line no-console
    console.log('[reservationSaveLogger] localStorage cleared');
  } catch {
    // ignore
  }
};

const summary = (): void => {
  const buf = readBuffer();
  const suspectCount = buf.filter((e) => e.suspect).length;
  // eslint-disable-next-line no-console
  console.log(
    '[reservationSaveLogger] total=%d suspect=%d',
    buf.length,
    suspectCount,
  );
  if (suspectCount > 0) {
    // eslint-disable-next-line no-console
    console.table(
      buf
        .filter((e) => e.suspect)
        .map((e) => ({
          ts: e.ts,
          fn: e.fn,
          id: e.reservation_id,
          status: e.status_resolved,
          base: e.price_fields.base_price,
          sum: e.price_fields.sum_price,
          option: e.price_fields.option_price,
        })),
    );
  }
};

// グローバルへ公開（DevTools から呼べるように）
if (typeof window !== 'undefined') {
  (window as any).__exportReservationSaveLog__ = exportLog;
  (window as any).__clearReservationSaveLog__ = clearLog;
  (window as any).__reservationSaveLogSummary__ = summary;
}
